import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import {
  applyGoogleMapsLocationToConfig,
  type GoogleMapsLocationSource,
} from '@up2stay/up2promo-commons/profile';

import type { TeamMember } from '@/core/types/profile-modules';

type TeamMembersModuleConfig = {
  members?: TeamMember[];
};

/** Removes the team-members module and reindexes order. */
export function omitTeamMembersModules<
  T extends { type?: string | null; order?: number },
>(modules: T[] | null | undefined): T[] {
  const list = modules ?? [];
  if (!list.some((module) => module.type === 'team_members')) {
    return list;
  }
  return list
    .filter((module) => module.type !== 'team_members')
    .map((module, index) => ({ ...module, order: index }));
}

function membersFromModuleConfig(
  config: ProfileModuleDto['config'] | undefined,
): TeamMember[] {
  if (!config || typeof config !== 'object') return [];
  const members = (config as TeamMembersModuleConfig).members;
  return Array.isArray(members) ? members : [];
}

function filterSelfMembers(params: {
  members: TeamMember[];
  selfUserId?: string | null;
  selfUsername?: string | null;
}): TeamMember[] {
  const selfUserId = params.selfUserId ?? null;
  const selfUsername = params.selfUsername ?? null;
  return params.members.filter((mem) => {
    const memberUserId = (mem.userId ?? mem.id ?? null) as string | null;
    const memberUsername = (mem.username ?? null) as string | null;
    if (selfUserId && memberUserId && memberUserId === selfUserId) return false;
    if (selfUsername && memberUsername && memberUsername === selfUsername)
      return false;
    return true;
  });
}

function locationFromLookup(
  locationsById:
    | ReadonlyMap<string, GoogleMapsLocationSource>
    | Record<string, GoogleMapsLocationSource>
    | undefined,
  locationId: string,
): GoogleMapsLocationSource | null {
  if (!locationsById) return null;
  if (locationsById instanceof Map) {
    return locationsById.get(locationId) ?? null;
  }
  return (
    (locationsById as Record<string, GoogleMapsLocationSource>)[locationId] ??
    null
  );
}

/**
 * Shared preview modules builder (ProfilesPage + ProfileEditPage).
 *
 * - Ensures team_members members are present when available
 * - Filters out the current user from team_members
 * - Resolves google_maps display fields from live workspace locations by id
 *
 * Note: linkability / external-link icon is handled in `TeamMembersModuleDisplay`
 * (it can derive the link from `workspaceUsername + member.username`).
 */
export function buildPreviewModules(params: {
  modules: ProfileModuleDto[] | null | undefined;
  selfUserId?: string | null;
  selfUsername?: string | null;
  /** Optional fallback members for team_members preview when module.config.members is empty. */
  teamMembersFallback?: TeamMember[];
  /**
   * Live workspace locations keyed by id. When present, google_maps modules
   * with `locationId` show current name/address/coords (not stored snapshots).
   */
  locationsById?:
    | ReadonlyMap<string, GoogleMapsLocationSource>
    | Record<string, GoogleMapsLocationSource>;
}): ProfileModuleDto[] | undefined {
  const modules = params.modules ?? undefined;
  if (!modules?.length) return modules;

  return modules.map((m) => {
    if (m.type === 'team_members') {
      const rawMembers = membersFromModuleConfig(m.config);
      const effectiveMembers =
        rawMembers.length > 0 ? rawMembers : (params.teamMembersFallback ?? []);
      const nextMembers = filterSelfMembers({
        members: effectiveMembers,
        selfUserId: params.selfUserId,
        selfUsername: params.selfUsername,
      });
      return { ...m, config: { ...(m.config ?? {}), members: nextMembers } };
    }

    if (m.type === 'google_maps') {
      const rawId = (m.config as { locationId?: unknown } | undefined)
        ?.locationId;
      const locationId =
        typeof rawId === 'string' && rawId.trim() ? rawId.trim() : '';
      if (!locationId) {
        return m;
      }
      return {
        ...m,
        config: applyGoogleMapsLocationToConfig(
          (m.config ?? {}) as Record<string, unknown>,
          locationFromLookup(params.locationsById, locationId),
        ),
      };
    }

    return m;
  });
}
